import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
  output
} from "@angular/core";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import * as echarts from "echarts";
import { v4 as uuidv4 } from "uuid";
import { GenericCounterGraphData } from "../../class/generic-counter-graph-data";
import { addHorizontalSwipeListener } from "../../class/horizontal-swipe-listener";
import { Platform, IonButton, IonIcon } from "@ionic/angular";
import { LocaleService } from "../../service/locale.service";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { LoggerService } from "src/service/logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { NgxEchartsDirective } from "ngx-echarts";
import { ErrorChartComponent } from "../error-chart/error-chart.component";

@Component({
    selector: "app-chart-line",
    templateUrl: "./chart-line.component.html",
    styleUrls: ["./chart-line.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonIcon, NgxEchartsDirective, ErrorChartComponent, TranslatePipe]
})
export class ChartLineComponent implements OnInit, OnDestroy {
  private readonly _translateService = inject(TranslateService);
  private readonly _platform = inject(Platform);
  private readonly _loggerService = inject(LoggerService);

  private _logContext: string = "Chart-line.component";
  private readonly _localeService = inject(LocaleService);
  private _removeSwipeListener: () => void = null;
  private _titleChart: string = "";
  public uniqueId: string;
  public lineChart: any;
  public showChart: boolean = false;
  public isMobileDevice: boolean = false;
  public nextCurrentMonth: boolean = false;

  private _currentDate = new Date();
  private _chartInitialized: boolean = false;
  private _intervalId: any;
  private _dataReady: boolean = false;

  @Input() skillVisible: boolean = true;
  @Input() enableSwipe: boolean = true;
  @Input() dataChart: GenericCounterGraphData = null;
  @Input() dataChartCSV: string = null;
  @Input() um: string = "";
  @Input() startDate: Date = null;
  @Input() endDate: Date = null;
  readonly emitUpdatedDate = output<Date>();
  @Input() errorChart: boolean = false;
  @Input() smartPalette: SmartPaletteCounter = null;
  @Input() chartName: string = "";
  @Input() counterName: string = "";

  constructor() {
    this.isMobileDevice =
      this._platform.is("ios") || this._platform.is("android");

    // The texts of the chart are written in the code: write them again in the new language
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateTitleChart());
  }

  async ngOnInit() {
    this.updateTitleChart();
    this.uniqueId = uuidv4();
    this.checkNextMonthGreaterThanCurrentMonth();
  }

  ngAfterViewInit() {
    this._intervalId = setInterval(() => {
      if (this.skillVisible && this._dataReady && !this._chartInitialized) {
        this.initChart();
        this._chartInitialized = true;
        clearInterval(this._intervalId);
      }
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.skillVisible) {
      this.showChart = true;
    } else {
      this.showChart = false;
    }
    if (changes.dataChart) {
      this._dataReady =
        this.dataChart &&
        Array.isArray(this.dataChart.data);
      this.updateTitleChart();
    }
    if (changes["dataChart"] && this.lineChart) {
      this.updateTitleChart();
      this.lineChart.setOption(this.generateOption());
    }
  }

  ngOnDestroy() {
    this._removeSwipeListener?.();
  }

  /**
   * Handle click to change to previous month
   */
  public onClickPreviousMonth() {
    this.goToPreviousMonth();
  }

  /**
   * Handle click to export CSV
   */
  public onClickExportCSV() {
    const fileName = (this.counterName + "_" + this.chartName).replace(/\s+/g, '_').toLocaleLowerCase() + ".csv"
    const blob = new Blob([this.dataChartCSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    link.click();

    window.URL.revokeObjectURL(url);
  }

  /**
   * Handle click to change to next month
   */
  public onClickNextMonth() {
    this.goToNextMonth();
  }

  /**
   * Check if next month is greater than current month
   */
  private checkNextMonthGreaterThanCurrentMonth() {
    this.nextCurrentMonth = false;
    if (
      this.startDate.getFullYear() == this._currentDate.getFullYear() &&
      this.startDate.getMonth() == this._currentDate.getMonth()
    ) {
      this.nextCurrentMonth = true;
    }
  }

  /**
   * Update title chart
   */
  private updateTitleChart() {
    this._titleChart = this._localeService.formatDate(this.startDate, "monthYear").toUpperCase();
    this.checkNextMonthGreaterThanCurrentMonth();
  }

  /**
   * Calculate chart option
   */
  private generateOption() {

    const xData = this.extractXData();
    const valueMin = this.extractValues("valueMin");
    const valueMax = this.extractValues("valueMax");

    return {
      title: {
        subtext: this._titleChart,
        left: "center",
        bottom: 10,
        subtextStyle: {
          fontSize: 14,
          color: this.smartPalette?.mainLableColor || "#FFFFFF",
          fontWeight: "bold",
        },
      },
      legend: this.createLegend(),
      xAxis: this.createXAxis(),
      yAxis: this.createYAxis(),
      series: this.createSeries(valueMin, valueMax),
      tooltip: this.createTooltip(),
      grid: {
        containLabel: true,
        left: 10,
        bottom: 40,
      },
    };
  }

  /**
   * Initialize chart
   */
  private initChart(): void {
    const chartDom = document.getElementById(this.uniqueId) as HTMLElement;
    if (chartDom) {
      this.lineChart = echarts.init(chartDom);
      const option = this.generateOption();
      this.lineChart.setOption(option);

      // Resizing the chart if you change window size without reloading the page
      window.addEventListener("resize", () => {
        this.lineChart.resize();
      });

      if (this.enableSwipe) {
        this.addSwipeListener();
      }
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "initChart()",
        `Element with id "${this.uniqueId}" not found.`
      );
    }
  }

  /**
   * Extract x-axis data
   */
  private extractXData(): number[] {
    return this.dataChart.data.map((d) => new Date(d.timeStampMin).getTime());
  }

  /**
   * Extract values for min/max
   */
  private extractValues(key: string): number[] {
    return this.dataChart.data.map((d) => {
      const parsedValue = parseFloat(d[key]);
      return isNaN(parsedValue) ? null : parsedValue;
    });
  }

  /**
   * Create legend configuration
   */
  private createLegend() {
    return {
      show: true,
      data: [this._translateService.instant("dictionary.minPeak"), this._translateService.instant("dictionary.maxPeak")],
      orient: "horizontal",
      top: this.isMobileDevice ? "top" : 10,
      left: this.isMobileDevice ? "center" : 0,
      itemGap: 20,
      textStyle: {
        color: this.smartPalette?.mainLableColor || "#ffffff",
        fontSize: 12,
      },
    };
  }

  /**
   * Create x-axis configuration
   */
  private createXAxis() {
    return {
      boundaryGap: false,
      type: "category",
      data: this.generateDateLabels(),
      axisLabel: {
        interval: 4,
        formatter: (value) => value,
        color: this.smartPalette?.mainLableColor || "#ffffff",
      },
      splitLine: {
        show: false,
        lineStyle: {
          color:
            this.smartPalette?.secondaryLableColor ||
            "rgba(255, 255, 255, 0.7)",
          width: 1,
          type: "solid",
        },
      },
      axisLine: {
        lineStyle: {
          color: this.smartPalette?.mainLableColor || "#ffffff",
          width: 2,
        },
      },
    };
  }

  /**
   * Create y-axis configuration
   */
  private createYAxis() {
    return [
      {
        type: "value",
        position: "left",
        nameLocation: "middle",
        boundaryGap: true,
        axisLine: {
          onZero: true,
          show: true,
          lineStyle: {
            color: this.smartPalette?.mainLableColor || "#ffffff",
            width: 2,
          },
        },
        axisLabel: {
          formatter: '{value} ' + this.um,
          color: this.smartPalette?.mainLableColor || "#ffffff",
        }
      }
    ];
  }

  /**
   * Create series configuration
   */
  private createSeries(valueMin: number[], valueMax: number[]) {
    return [
      {
        name: this._translateService.instant("dictionary.minPeak"),
        type: "line",
        data: this.combineData(valueMin),
        smooth: false,
        symbol: "circle",
        itemStyle: {
          color: this.smartPalette?.graphPointMin || "#FFFFFF",
        },
        lineStyle: {
          color: this.smartPalette?.graphLineMin || "#d8ede7",
        },
      },
      {
        name: this._translateService.instant("dictionary.maxPeak"),
        type: "line",
        data: this.combineData(valueMax),
        smooth: false,
        symbol: "circle",
        itemStyle: {
          color: this.smartPalette?.graphPointMax || "#02476E",
        },
        lineStyle: {
          color: this.smartPalette?.graphLineMax || "#035E91",
        },
      },
    ];
  }

  /**
   * Combine values with x-axis labels
   */
  private combineData(values: number[]): Array<[string, number]> {
    return values.map((val, i) => [this.generateDateLabels()[i], val]);
  }

  /**
   * Create tooltip configuration
   */
  private createTooltip() {
    return {
      trigger: "axis",
      textStyle: {
        color: "#000000",
      },
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      formatter: (params) => {
        const date = params[0].data[0];
        const seriesData = params
          .map((param) => {
            const marker = `<span style="display:inline-block;margin-right:5px;width:9px;height:9px;border-radius:50%;border:1px solid #888;background-color:${param.color};"></span>`;
            return `${marker} ${param.seriesName}: ${param.data[1] !== null
              ? param.data[1].toString().replace(".", ",") + " " + this.um
              : "N/A"
              }`;
          })
          .join("<br/>");
        return `${date}<br/>${seriesData}`;
      },
    };
  }

  /**
   * Generate date labels for the chart for series data
   * @returns
   */
  private generateDateLabels() {
    const labels = [];
    const currentDate = new Date(this.startDate);
    while (currentDate <= this.endDate) {
      const day = String(currentDate.getDate()).padStart(2, "0");
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const year = currentDate.getFullYear();
      labels.push(`${day}/${month}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return labels;
  }

  /**
   * Listener for swipe event
   */
  private addSwipeListener(): void {
    const chartDom = document.getElementById(this.uniqueId);
    if (chartDom && this.isMobileDevice) {
      this._removeSwipeListener = addHorizontalSwipeListener(
        chartDom,
        () => {
          if (!this.nextCurrentMonth) {
            this.goToNextMonth();
          }
        },
        () => {
          this.goToPreviousMonth();
        }
      );
    }
  }

  /**
   * Set start and end date for the month
   * @param date
   */
  private setDates(date) {
    this.startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    this.endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  /**
   * Go to next month
   */
  private goToNextMonth() {
    this.setDates(
      new Date(this.startDate.getFullYear(), this.startDate.getMonth() + 1)
    );
    this.emitUpdatedDate.emit(this.startDate);
  }

  /**
   * Go to previous month
   */
  private goToPreviousMonth() {
    this.setDates(
      new Date(this.startDate.getFullYear(), this.startDate.getMonth() - 1)
    );
    this.emitUpdatedDate.emit(this.startDate);
  }
}
